// ScenarioPicker — autoplay-able demo arc switching between Princeton archetypes.
// Lets prospective athletes/coaches see EVERY engine behavior in 60 seconds:
//   • Coleman → healthy starter with mid-week pain → recovery
//   • Liam    → clean game week, no issues
//   • Wyatt   → heavy reliever accumulating multi-day fatigue
//   • Jasper  → returning from rehab, ramping back through E→D→C→B
import { useEffect, useMemo, useState } from "react";
import { Play, Pause, ChevronRight, Sparkles, RotateCcw, Link2, Check } from "lucide-react";

const BANDS = {
  A: "#D90429", B: "#F59E0B", C: "#3B82F6", D: "#22D3EE", E: "#16A34A",
};

export const ARCHETYPES = [
  {
    id: "coleman",
    name: "Coleman Standish",
    headline: "Healthy starter · mid-week pain → recovery",
    scenarios: [
      { id: "d0", label: "Day 0 — Game day", title: "Healthy ramp — Game-day Hybrid A",
        score: 84, hybrid: "A", pitch_count: 92,
        note: "HRV 68 (above 62 baseline), 7.6 hrs sleep, no soreness. ArmIQ greenlights Hybrid A — Long Toss + Pull-Downs. He starts.",
        sub: { recovery: 92, load: 78, wellness: 90, mechanics: 76 } },
      { id: "d4", label: "Day 4 — Bullpen", title: "Heavy bullpen — Hybrid B with caution",
        score: 62, hybrid: "B", pitch_count: 38,
        note: "Acute load climbing — 92 pitches Sunday + 38-throw bullpen Wednesday. HRV dipped to 56. Engine slides him to Hybrid B (no pull-downs).",
        sub: { recovery: 70, load: 52, wellness: 78, mechanics: 70 } },
      { id: "d5", label: "Day 5 — Yellow flag", title: "Pain during throwing — auto-shutdown to D",
        score: 38, hybrid: "D", pitch_count: 0,
        note: "Coleman logs 3/5 arm health + 'pain during throwing'. Engine pins him to Hybrid D regardless of score. Coach + AT get a critical push.",
        sub: { recovery: 45, load: 38, wellness: 60, mechanics: 35 } },
      { id: "d6", label: "Day 6 — Cleared", title: "All clear — back to Hybrid A",
        score: 81, hybrid: "A", pitch_count: 0,
        note: "24 hrs rest + Phase 4 mobility & Full Body Yoga. HRV 65, soreness 1/5, no pain. ArmIQ clears him back to Hybrid A. Next start is on.",
        sub: { recovery: 88, load: 72, wellness: 84, mechanics: 80 } },
    ],
  },
  {
    id: "liam",
    name: "Liam Costa",
    headline: "Healthy game week · no issues",
    scenarios: [
      { id: "d1", label: "Day 1 — Game", title: "Clean start — Hybrid A",
        score: 88, hybrid: "A", pitch_count: 96,
        note: "Career-high HRV 74, 8.1 hrs sleep, fully recovered from last start. Hybrid A: throws 96 pitches over 6.1 IP.",
        sub: { recovery: 94, load: 80, wellness: 92, mechanics: 84 } },
      { id: "d2", label: "Day 2 — Recovery", title: "Active recovery — Hybrid B",
        score: 70, hybrid: "B", pitch_count: 0,
        note: "Standard post-start fatigue, no soreness. Long toss only, no pull-downs. Phase 4 mobility + Full Body Yoga session.",
        sub: { recovery: 76, load: 60, wellness: 80, mechanics: 78 } },
      { id: "d3", label: "Day 3 — Bullpen tune", title: "Short long toss — Hybrid C",
        score: 64, hybrid: "C", pitch_count: 22,
        note: "Standard Day 3 — 90/120 ft short long toss to maintain feel. Player flexed UP from suggested D because arm felt live. Coach sees the override.",
        sub: { recovery: 72, load: 58, wellness: 78, mechanics: 76 } },
      { id: "d5", label: "Day 5 — Next outing", title: "Back to Hybrid A",
        score: 86, hybrid: "A", pitch_count: 94,
        note: "Recovery curve held — HRV back to 72, fresh legs. Coach pencils him in for the Saturday start.",
        sub: { recovery: 90, load: 78, wellness: 88, mechanics: 82 } },
    ],
  },
  {
    id: "wyatt",
    name: "Wyatt Cole",
    headline: "Heavy reliever · multi-day fatigue accumulation",
    scenarios: [
      { id: "g1", label: "Game 1 — 2-inning hold", title: "Back-to-back outing — Hybrid B",
        score: 68, hybrid: "B", pitch_count: 32,
        note: "Came in for the 7th + 8th, 32 pitches. Tight game, max effort late. Engine logs the acute load and warns about bullpen usage.",
        sub: { recovery: 72, load: 58, wellness: 78, mechanics: 80 } },
      { id: "g2", label: "Game 2 — 1 inning save", title: "Stretching the elastic — Hybrid C",
        score: 58, hybrid: "C", pitch_count: 18,
        note: "Closed out the win next night. A:C load ratio climbing (1.4x). ArmIQ moves to short long toss tomorrow — no max-effort pulls.",
        sub: { recovery: 64, load: 48, wellness: 70, mechanics: 76 } },
      { id: "g3", label: "Day 3 — Hot HRV drop", title: "Engine pulls him — Hybrid D",
        score: 42, hybrid: "D", pitch_count: 0,
        note: "HRV down 28% vs baseline + RHR +6 + 6.1 hrs sleep. Hot-Pitcher tile from yesterday's outing is still showing. ArmIQ blocks any throwing — ROM + light toss only.",
        sub: { recovery: 48, load: 35, wellness: 55, mechanics: 70 } },
      { id: "g5", label: "Day 5 — Reset", title: "Cleared again — Hybrid B",
        score: 74, hybrid: "B", pitch_count: 18,
        note: "Two days of forced recovery — HRV back to 64 (above his 62 baseline), soreness 1/5. Coach can use him in a tight game tonight.",
        sub: { recovery: 82, load: 68, wellness: 82, mechanics: 78 } },
    ],
  },
  {
    id: "jasper",
    name: "Jasper Vega",
    headline: "Returning from rehab · structured ramp E → D → C → B",
    scenarios: [
      { id: "w1", label: "Week 1 — Restart", title: "ROM only — Hybrid E",
        score: 28, hybrid: "E", pitch_count: 0,
        note: "Cleared by medical, but acute load reset to zero. ArmIQ pins him to Hybrid E — ROM throws only, no pull-downs, no actual catching session. AT verifies attendance.",
        sub: { recovery: 70, load: 0, wellness: 65, mechanics: 22 } },
      { id: "w2", label: "Week 2 — Light toss", title: "Adding load — Hybrid D",
        score: 44, hybrid: "D", pitch_count: 0,
        note: "Two weeks of clean logs + verified AT sessions. Engine eases him into Hybrid D — ROM + light toss out to 60 ft. RPE stays ≤ 5.",
        sub: { recovery: 75, load: 30, wellness: 70, mechanics: 32 } },
      { id: "w3", label: "Week 3 — Short LT", title: "First throwing day — Hybrid C",
        score: 58, hybrid: "C", pitch_count: 0,
        note: "Three weeks pain-free, HRV trending up, chronic load building. Hybrid C — 90/120 ft short long toss, no max pulls. Coach watches the Hot-Pitcher tile during the session.",
        sub: { recovery: 80, load: 50, wellness: 78, mechanics: 50 } },
      { id: "w4", label: "Week 4 — Full LT", title: "Cleared to compete — Hybrid B",
        score: 76, hybrid: "B", pitch_count: 0,
        note: "Full long-toss program, no pull-downs yet. Next bullpen will be Hybrid A. Athletic Trainer signs off — back in the rotation by week 5.",
        sub: { recovery: 86, load: 65, wellness: 82, mechanics: 70 } },
    ],
  },
];

export default function ScenarioPicker({ onScenarioChange, autoplayMs = 4500 }) {
  const [athleteIdx, setAthleteIdx] = useState(0);
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shared, setShared] = useState(false);

  const athlete = ARCHETYPES[athleteIdx];
  const s = athlete.scenarios[scenarioIdx];
  const scenarios = useMemo(() => athlete.scenarios, [athlete]);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/demo/${athlete.id}/${s.id}`;
  }, [athlete.id, s.id]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `ArmIQ demo — ${athlete.name}`,
          text: s.title,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    } catch (e) {
      /* ignore — user dismissed share sheet */
    }
  };

  useEffect(() => {
    onScenarioChange?.(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteIdx, scenarioIdx]);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      setScenarioIdx((i) => (i + 1) % scenarios.length);
    }, autoplayMs);
    return () => clearTimeout(t);
  }, [playing, scenarioIdx, scenarios.length, autoplayMs]);

  const onPickAthlete = (i) => {
    setPlaying(false);
    setAthleteIdx(i);
    setScenarioIdx(0);
  };

  return (
    <div
      className="rounded-xl p-4"
      style={{ backgroundColor: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.35)" }}
      data-testid="scenario-picker"
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 shrink-0" style={{ color: "var(--brand-secondary)" }} />
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase truncate" style={{ color: "var(--brand-secondary)" }}>
              Demo · {athlete.name}
            </div>
            <div className="font-display font-bold text-white text-sm leading-tight mt-0.5 truncate">{s.title}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={handleShare} className="p-1.5 rounded hover:bg-white/10"
                  style={{ color: shared ? "#34D399" : "rgba(255,255,255,0.7)" }}
                  title={shared ? "Link copied!" : "Share this scenario"}
                  data-testid="scenario-share">
            {shared ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
          </button>
          <button onClick={() => setPlaying((p) => !p)} className="p-1.5 rounded hover:bg-white/10 text-white/70"
                  title={playing ? "Pause" : "Autoplay"} data-testid="scenario-play-toggle">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button onClick={() => { setPlaying(false); setScenarioIdx(0); }} className="p-1.5 rounded hover:bg-white/10 text-white/70"
                  title="Reset" data-testid="scenario-reset">
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Athlete archetype picker */}
      <div className="flex items-center gap-1.5 mt-3 overflow-x-auto" data-testid="scenario-athletes">
        {ARCHETYPES.map((a, i) => {
          const active = i === athleteIdx;
          return (
            <button
              key={a.id}
              onClick={() => onPickAthlete(i)}
              data-testid={`scenario-athlete-${a.id}`}
              className="shrink-0 rounded-md px-2.5 py-1.5 text-[10px] font-bold tracking-[0.1em] uppercase transition"
              style={{
                backgroundColor: active ? "rgba(34,211,238,0.18)" : "transparent",
                border: `1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(255,255,255,0.15)"}`,
                color: active ? "white" : "rgba(255,255,255,0.55)",
              }}
            >
              {a.name.split(" ")[0]}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-white/40 mt-1 italic">{athlete.headline}</p>

      {/* Scenario day tabs */}
      <div className="flex items-center gap-1.5 mt-2 overflow-x-auto" data-testid="scenario-tabs">
        {scenarios.map((sc, i) => {
          const active = i === scenarioIdx;
          return (
            <button
              key={sc.id}
              onClick={() => { setPlaying(false); setScenarioIdx(i); }}
              data-testid={`scenario-${sc.id}`}
              className="shrink-0 rounded px-2 py-1 text-[10px] font-extrabold tracking-[0.12em] uppercase transition"
              style={{
                backgroundColor: active ? BANDS[sc.hybrid] + "22" : "transparent",
                border: `1px solid ${active ? BANDS[sc.hybrid] : "rgba(255,255,255,0.15)"}`,
                color: active ? "white" : "rgba(255,255,255,0.55)",
              }}
            >
              {sc.label} <ChevronRight className="h-2.5 w-2.5 inline" />
              <span style={{ color: BANDS[sc.hybrid] }}>{sc.hybrid}</span>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-white/65 mt-3">{s.note}</p>

      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        <Stat label="Score" value={s.score} />
        <Stat label="Hybrid" value={s.hybrid} color={BANDS[s.hybrid]} />
        <Stat label="Pitches" value={s.pitch_count} />
        <Stat label="Recovery" value={`${s.sub.recovery}%`} />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded border border-white/10 bg-white/5 p-2">
      <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{label}</div>
      <div className="font-display font-black text-xl mt-0.5" style={{ color: color || "white" }}>{value}</div>
    </div>
  );
}
